import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from './components/AppLayout';
import { CapacitorBridge } from './components/CapacitorBridge';
import { PushPermissionPrompt } from './components/PushPermissionPrompt';
import { fetchMe, type MeResponse } from './lib/api';
import { useSession } from './lib/auth';
import { MeContext } from './lib/me-context';
import { checkPushPermission, initPushNotifications } from './lib/push';
import { AuditLog } from './pages/AuditLog';
import { Conversation } from './pages/Conversation';
import { Founder } from './pages/Founder';
import { FounderAudit } from './pages/FounderAudit';
import { FounderDiagnostics } from './pages/FounderDiagnostics';
import { FounderDocs } from './pages/FounderDocs';
import { FounderFlags } from './pages/FounderFlags';
import { FounderInstitutions } from './pages/FounderInstitutions';
import { FounderModerators } from './pages/FounderModerators';
import { InstitutionOnboarding } from './pages/InstitutionOnboarding';
import { Guidelines } from './pages/Guidelines';
import { Home } from './pages/Home';
import { Landing } from './pages/Landing';
import { Moderation } from './pages/Moderation';
import { ModerationEvent } from './pages/ModerationEvent';
import { ModerationUser } from './pages/ModerationUser';
import { NotificationSettings } from './pages/NotificationSettings';
import { Onboarding } from './pages/Onboarding';
import { Outcomes } from './pages/Outcomes';
import { Reflections } from './pages/Reflections';
import { Privacy } from './pages/Privacy';
import { Rejected } from './pages/Rejected';
import { RestrictedUsers } from './pages/RestrictedUsers';
import { ReviewQueue } from './pages/ReviewQueue';
import { Safety } from './pages/Safety';
import { Terms } from './pages/Terms';
import { UnsupportedCommunity } from './pages/UnsupportedCommunity';

function Loading() {
  return (
    <main>
      <div className="card">
        <p className="muted">Loading…</p>
      </div>
    </main>
  );
}

export function App() {
  const { data: session, isPending } = useSession();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [meLoading, setMeLoading] = useState(true);

  const loadMe = useCallback(() => {
    setMeLoading(true);
    fetchMe()
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setMeLoading(false));
  }, []);

  useEffect(() => {
    if (session) loadMe();
  }, [session, loadMe]);

  // Once the user is signed in AND onboarded, quietly register for push
  // IF the OS already granted it (returning user, fresh app launch). The
  // 'prompt' (undetermined) case is handled by <PushPermissionPrompt />
  // which shows a branded rationale before firing the OS dialog — never
  // surface the OS dialog cold on first authenticated render.
  useEffect(() => {
    if (me && me.user.status === 'active' && me.memberships.length > 0) {
      void (async () => {
        const state = await checkPushPermission();
        if (state === 'granted') void initPushNotifications();
      })();
    }
  }, [me]);

  // Public legal/safety pages must be reachable from any state — a
  // student forwarding a sign-up link to a friend may click /privacy
  // before they've ever signed in, and a moderator removing a user
  // for cause should be able to point at /guidelines from any context.
  // The legal pages own their own minimal chrome via <LegalPage>, so
  // they render outside the authed AppLayout.
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  if (path === '/institution-onboarding') return <InstitutionOnboarding />;
  if (path === '/privacy') return <Privacy />;
  if (path === '/terms') return <Terms />;
  if (path === '/safety') return <Safety />;
  if (path === '/guidelines') return <Guidelines />;

  if (isPending) return <Loading />;
  if (!session) return <Landing />;
  if (meLoading) return <Loading />;
  if (!me) return <Landing />;

  // Minors detected at the age gate are engineered out — no membership, ever.
  if (me.user.ageVerificationStatus === 'rejected_minor') return <Rejected />;
  if (me.memberships.length === 0) {
    // If we have a magic-link account but the email no longer maps to any
    // community (institution offboarded, founder bypass missing, etc.),
    // route to the lead-capture screen instead of a broken onboarding.
    if (!me.eligibleCommunity) {
      const email = (session.user as { email?: string }).email ?? '';
      return <UnsupportedCommunity email={email} />;
    }
    return <Onboarding me={me} onComplete={loadMe} />;
  }

  return (
    <BrowserRouter>
      <MeContext.Provider value={me}>
        <CapacitorBridge onResume={loadMe} />
        <PushPermissionPrompt />
        <Routes>
        <Route element={<AppLayout />}>
          <Route path="/" element={<Home me={me} />} />
          <Route path="/conversations/:id" element={<Conversation me={me} />} />
          <Route path="/reflections" element={<Reflections me={me} />} />
          <Route path="/settings/notifications" element={<NotificationSettings />} />
          {me.user.isModerator && (
            <>
              <Route path="/moderation" element={<ReviewQueue me={me} />} />
              <Route path="/moderation/events/:id" element={<ModerationEvent me={me} />} />
              <Route path="/moderation/users/:userId" element={<ModerationUser me={me} />} />
              <Route path="/moderation/matching" element={<Moderation me={me} />} />
              <Route path="/moderation/audit" element={<AuditLog me={me} />} />
              <Route path="/moderation/outcomes" element={<Outcomes me={me} />} />
              <Route path="/moderation/restricted" element={<RestrictedUsers me={me} />} />
            </>
          )}
          {me.user.isFounder && (
            <>
              <Route path="/founder" element={<Founder />} />
              <Route path="/founder/moderators" element={<FounderModerators />} />
              <Route path="/founder/audit" element={<FounderAudit />} />
              <Route path="/founder/flags" element={<FounderFlags />} />
              <Route path="/founder/diagnostics" element={<FounderDiagnostics />} />
              <Route path="/founder/docs" element={<FounderDocs />} />
              <Route path="/founder/institutions" element={<FounderInstitutions />} />
            </>
          )}
        </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </MeContext.Provider>
    </BrowserRouter>
  );
}
